"use client"

import type { Offer } from "@local/offer/src/types"
import { getT } from "@local/i18n/src/t"
import { getLanguageForMarket } from "@local/i18n/src/languages"
import { buy } from "@local/i18n/src/catalogs/buy"
import { pdp } from "@local/i18n/src/catalogs/pdp"
import { CreditCardsPayPal } from "../../ui/CreditCards/CreditCardsPayPal"
import { cn } from "@local/utils/src/cn"
import { getCdnUrl } from "@local/utils/src/getCdnUrl"
import Image from "next/image"
import { barlowCondensed } from "../../../utils/fonts"
import { Button } from "../../ui/Button"
import type { Route } from "next"
import { ContentBlock } from "../../ui/ContentBlock"
import { CallToAction } from "@local/offer/src/modules/common/ButtonBuy/CallToAction"
import { useSearchParams } from "next/navigation"

import { type ReactNode, Suspense, useMemo } from "react"
import { VideoPlayer } from "../../ui/VideoPlayer"

export interface PromoHeadlineVideoWithStarsProps {
  content: Offer.PromoHeadlineVideoWithStars
  market?: string
  product?: Offer.PinProduct
  prices?: Offer.ListingPrices
  trustpilotSlot?: ReactNode
  /** Set for above-the-fold placements (e.g. first builder block) so the fallback image preloads as the LCP candidate. */
  priority?: boolean
}

const VideoFallBack = ({
  content,
  market,
  priority,
}: PromoHeadlineVideoWithStarsProps) => {
  const t = getT(getLanguageForMarket(market), { pdp })
  if (content.fallbackImage) {
    return (
      <Image
        alt={"BÆRSkin Tactical Supply Co."}
        height={content.fallbackImage.height || 1440}
        src={getCdnUrl({
          src: content.fallbackImage.url as string,
          width: content.fallbackImage.width || 1920,
        })}
        width={content.fallbackImage.width || 1440}
        sizes="100vw"
        priority={priority}
        className="absolute top-0 z-10 h-full w-full object-cover"
      />
    )
  }
  return <div>{t("pdp.loadingPlayer")}</div>
}

export const VideoAndImage = ({
  content,
  market,
  priority,
}: PromoHeadlineVideoWithStarsProps) => {
  // disable video if videoProvider is not bunny
  if (content.videoProvider !== "bunny") {
    return null
  }
  return (
    <div className="absolute inset-0 z-10 h-full w-full overflow-hidden">
      <div
        className={cn(
          "absolute inset-0 z-30 h-full w-full",
          // content.brightBackground ? "" : "bg-black/30"
          content.brightBackground ? "bg-white/50" : "bg-black/30"
        )}
      />
      {content.videoId ? (
        <div className="absolute inset-0 z-20 h-full w-full [&_video]:aspect-auto! [&_video]:h-full [&_video]:w-full [&_video]:object-cover [&>div]:h-full [&>div]:w-full">
          <VideoPlayer
            videoId={content.videoId}
            poster={
              content.fallbackImage?.url
                ? getCdnUrl({ src: content.fallbackImage.url })
                : ""
            }
            muted={true}
            loop={true}
            controls={false}
            autoplay={true}
            priority={!!priority}
            loadStrategy="idle"
          />
        </div>
      ) : (
        <VideoFallBack content={content} market={market} priority={priority} />
      )}
    </div>
  )
}

export const TextContent = ({
  content,
  market,
  product,
  prices,
  trustpilotSlot,
}: PromoHeadlineVideoWithStarsProps) => {
  const searchParams = useSearchParams()
  const itemId = searchParams?.get("item_id")

  // Calculate discount percentage based on modifier prices
  const discount = useMemo(() => {
    if (!product) return undefined

    const originalPrice = prices?.[0]?.value ?? product.msrp
    const rawDiscount = prices?.[0]?.discountValue
    const defaultDiscountPrice =
      rawDiscount && rawDiscount > 0 ? rawDiscount : originalPrice

    // Try to find matching SKU from item_id in URL or preselected SKU
    let matchedModifierPrice: number | undefined
    let hasSpecificSkuMatch = false

    // Get all SKUs from the product
    const allSkus =
      product.type === "BUNDLE"
        ? product.skuLists.flatMap((skuList) => skuList.skus)
        : product.skus

    // Find SKU - first check item_id, then check for preselected
    const matchingSku = itemId
      ? allSkus.find(
          (sku) =>
            sku.legacyId === itemId ||
            sku.attributes.some(
              (attr) => attr.name === "payloadId" && attr.value === itemId
            ) ||
            sku.id === itemId ||
            sku.name === itemId
        )
      : allSkus.find((sku) => sku.isPreselected === true)

    if (matchingSku) {
      hasSpecificSkuMatch = true

      // Check if this SKU has a modifier price
      if (matchingSku.name && prices?.[0]?.modifiers?.length) {
        const matchedModifier = prices[0].modifiers.find((modifier) =>
          modifier.skus.includes(matchingSku.name as string)
        )

        if (matchedModifier) {
          matchedModifierPrice = matchedModifier.amount
        }
      }
    }

    // Determine final discount price
    let discountPrice: number

    if (hasSpecificSkuMatch) {
      discountPrice = matchedModifierPrice ?? defaultDiscountPrice
    } else {
      const lowestModifierPrice = prices?.[0]?.modifiers?.length
        ? prices[0].modifiers.reduce(
            (min, m) => (m.amount < min ? m.amount : min),
            prices[0].modifiers[0].amount
          )
        : undefined

      discountPrice =
        lowestModifierPrice && lowestModifierPrice < defaultDiscountPrice
          ? lowestModifierPrice
          : defaultDiscountPrice
    }

    return Math.round(((originalPrice - discountPrice) / originalPrice) * 100)
  }, [itemId, prices, product])

  return (
    <>
      <div className="absolute bottom-0 z-30 h-32 w-full bg-linear-to-b from-transparent to-gray-950 sm:hidden" />
      <div className="relative w-full bg-gray-950 px-6 py-10 lg:py-20">
        <div
          className={cn(
            "z-10 mx-auto flex w-full max-w-7xl flex-col items-center justify-center md:items-start md:justify-start md:px-16 lg:my-10",
            content.brightBackground ? "text-gray-900" : "text-white"
          )}
        >
          <div className="z-0 h-[260px] sm:hidden" />
          {content.headline && (
            <h2
              className={cn(
                "leading-tighter z-40 mb-4 w-fit text-center text-6xl font-medium uppercase md:text-left",
                barlowCondensed.className
              )}
              dangerouslySetInnerHTML={{
                __html: content.headline,
              }}
            />
          )}
          <p className="leading-body z-40 mb-5 max-w-[424px] text-center text-base font-semibold md:text-left">
            {content.subtitle}
          </p>
          {content.ctaContent && (
            <div className="z-40">
              <Button
                as="link"
                href={`/${market}${content.ctaUrl}` as Route}
                prefetch={false}
                className="w-full max-w-[360px] bg-red-600 font-medium uppercase hover:bg-red-700"
              >
                <CallToAction
                  input={content.ctaContent}
                  action={getT(getLanguageForMarket(market), { buy })(
                    "buy.shopNow"
                  )}
                  market={market}
                  discount={discount}
                />
              </Button>
            </div>
          )}
          {content.showReviewStars && trustpilotSlot && (
            <div className="z-40 -mt-1">{trustpilotSlot}</div>
          )}
          {content.showCreditCards && (
            <div className="mx-auto max-w-[500px] rounded-full bg-white px-2 py-1 md:mx-0">
              <CreditCardsPayPal />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export const PromoHeadlineVideoWithStars = ({
  content,
  market,
  product,
  prices,
  trustpilotSlot,
  priority,
}: PromoHeadlineVideoWithStarsProps) => {
  return (
    <ContentBlock
      className="relative flex h-full justify-center overflow-hidden"
      touchEdges
    >
      <VideoAndImage content={content} market={market} priority={priority} />
      <Suspense>
        <TextContent
          content={content}
          market={market}
          product={product}
          prices={prices}
          trustpilotSlot={trustpilotSlot}
        />
      </Suspense>
    </ContentBlock>
  )
}
